import { useEffect, useMemo, useState } from "react"
import { cn } from "@ui/utils"
import { isBefore, setHours } from "date-fns"

import { useFetchCurrentUser } from "@/hooks/api/users"

// A greeting optionally ends with a question mark. We flag those variants so
// the component can add the correct punctuation after the user's name.
interface Greeting {
  text: string
  appendName?: boolean
}

const morningGreetings: Greeting[] = [{ text: "Good morning, ", appendName: true }]

const afternoonGreetings: Greeting[] = [{ text: "Good afternoon, ", appendName: true }]

const eveningGreetings: Greeting[] = [
  { text: "Good evening, ", appendName: true },
  { text: "We work late...ask away." },
  { text: "Good evening, ", appendName: true },
  { text: "How can I help?" },
  { text: "Good evening, ", appendName: true },
]

export default function AssistantGreeting({
  hasStartedChat,
  className,
  titleClassName,
}: {
  hasStartedChat: boolean
  className?: string
  titleClassName?: string
}) {
  const [isShowing, setIsShowing] = useState(true)
  const { data: user } = useFetchCurrentUser()

  useEffect(() => {
    if (hasStartedChat) {
      setTimeout(() => {
        setIsShowing(false)
      }, 500)
    }
  }, [hasStartedChat])

  function getRandomGreeting(): Greeting {
    const now = new Date()
    const isVeryLate = isBefore(now, setHours(now, 3))
    const isMorning = isBefore(now, setHours(now, 12))
    const isAfternoon = isBefore(now, setHours(now, 19))

    let greetings: Greeting[]

    if (isVeryLate) {
      greetings = eveningGreetings
    } else if (isMorning) {
      greetings = morningGreetings
    } else if (isAfternoon) {
      greetings = afternoonGreetings
    } else {
      greetings = eveningGreetings
    }

    const randomIndex = Math.floor(Math.random() * greetings.length)
    return greetings[randomIndex]
  }

  // Memoize the greeting so it only changes on initial mount (or on hot reload)
  const greeting = useMemo(() => getRandomGreeting(), [])

  return (
    <div className={cn("flex w-full px-2", className)}>
      <h3
        className={cn(
          "font-rework short:pb-1 short:text-3xl shorter:text-2xl pb-2 text-5xl font-bold transition-all duration-200",
          titleClassName,
          !isShowing && "opacity-0"
        )}
      >
        {`${greeting.text}${greeting.appendName ? user?.first_name : ""}`}
      </h3>
    </div>
  )
}
